import React, { useState } from 'react'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools, voiceCollection } from '../voice/buildVoiceTools'
import { VoiceUITool } from '../voice/types'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/EditOutlined'
import EventIcon from '@mui/icons-material/EventOutlined'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import PauseIcon from '@mui/icons-material/PauseCircleOutline'
import PlayArrowIcon from '@mui/icons-material/PlayCircleOutline'
import RunNowIcon from '@mui/icons-material/PlayArrow'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useAlerts, { type CreateAlertBody, type UpdateAlertBody } from '../hooks/useAlerts'
import useAlertForm from '../hooks/useAlertForm'
import useAlertSetupSession from '../hooks/useAlertSetupSession'
import useAiAccess from '../hooks/useAiAccess'
import useWhoToAsk from '../hooks/useWhoToAsk'
import AlertFormDialog from '../components/alerts/AlertFormDialog'
import DisabledReason from '../components/system/DisabledReason'
import TierSection from '../components/dashboard/TierSection'
import { alertConditionLockedReason, alertSetupReason, colleagueAlertReason, isAlertOwner } from '../utils/alertPermissions'
import { alertSourceOf, kindChipLabel } from '../utils/alertSources'
import { describeSchedule } from '../utils/alertSchedule'
import { extractErrorMessage } from '../utils/apitools'
import DeleteConfirmWindow from '../components/system/DeleteConfirmWindow'
import PageHeader from '../components/system/PageHeader'
import PageAgentButton from '../agent/components/PageAgentButton'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'
import ActionButton from '../components/system/ActionButton'
import { AlertRule, AlertRuleWithLatest, AlertRuleStatus, AlertEvaluationResult } from '../types/gotypes'
import { useDisplayNames } from '../utils/displayNames'

const STATUS_CHIP_COLOR: Record<AlertRuleStatus, 'success' | 'warning' | 'default'> = {
  active: 'success',
  paused: 'warning',
  archived: 'default',
}

// Matches AlertDetailPage's RESULT_CHIP_COLOR — the same chip, same colours,
// wherever a result is shown (T18).
const RESULT_CHIP_COLOR: Record<AlertEvaluationResult, 'error' | 'success' | 'warning'> = {
  running: 'warning',
  triggered: 'error',
  not_triggered: 'success',
  error: 'warning',
}

/**
 * One row control: drawn disabled with `reason` when the viewer may not use
 * it (DisabledReason says why and who to ask), else with its usual tooltip.
 */
const RowControl: React.FC<{ reason?: string; tooltip: string; children: React.ReactElement }> = ({ reason, tooltip, children }) =>
  reason ? (
    <DisabledReason reason={reason}>{children}</DisabledReason>
  ) : (
    <Tooltip title={tooltip}>
      <span>{children}</span>
    </Tooltip>
  )

/**
 * Alerts (alerts round, 12 Oct): two tiers, "Mine" and "My colleagues'",
 * under the chat-visibility rule (the server lists only the colleagues'
 * alerts the viewer may see). New alert opens the agent start screen with
 * the alert card; "Watch an event" opens the dialog, since an event alert
 * needs no agent.
 */
const AlertsPage: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const [deleteTarget, setDeleteTarget] = useState<AlertRule | null>(null)
  const [statusFilter, setStatusFilter] = useState<AlertRuleStatus | ''>('')
  const [mineOpen, setMineOpen] = useState(true)
  const [colleaguesOpen, setColleaguesOpen] = useState(true)
  // The Alerts helper (Douglas plan T14).
  const [helperOpen, setHelperOpen] = useState(false)

  const customer = router.params.customer || account.customer?.name || ''
  const viewerEmail = account.user?.session_info?.email || ''
  const isManager = !!account.user?.role_permissions?.isManager

  const {
    alerts,
    listIsLoading,
    listIsError,
    listError,
    createAlert,
    createIsLoading,
    updateAlert,
    updateIsLoading,
    deleteAlert,
    deleteIsLoading,
    evaluateAlert,
    evaluatingAlertId,
  } = useAlerts({ customer })

  const alertForm = useAlertForm(customer)
  const setup = useAlertSetupSession(customer)
  const aiAccess = useAiAccess(customer)
  const whoToAsk = useWhoToAsk(customer)
  const sessionJobs = (account.customer?.sessionJobs || []).filter((j): j is { name: string; displayName?: string } => !!j.name)

  const createReason = aiAccess.can('alert_create') ? undefined : aiAccess.refusalFor('alert_create')
  const setupReason = aiAccess.can('agent_sessions') ? undefined : aiAccess.refusalFor('agent_sessions')
  // New alert saves a rule (alert_create) and opens a chat (agent_sessions).
  const newAlertReason = createReason ?? setupReason
  const editingAlert = alertForm.form?.mode === 'edit' ? alertForm.form.alert : undefined

  const filteredAlerts = statusFilter
    ? alerts.filter(a => a.status === statusFilter)
    : alerts
  const mine = filteredAlerts.filter((a) => isAlertOwner(a, viewerEmail))
  const colleagues = filteredAlerts.filter((a) => !isAlertOwner(a, viewerEmail))

  // New alert: the agent start screen with an empty alert card. Nothing is
  // saved until Send there.
  const handleNewAlertClick = () => {
    if (!customer || newAlertReason) return
    setup.startNew()
  }

  // An event alert spends nothing, so it opens without AI access.
  const handleWatchEventClick = () => {
    if (!customer) return
    const current = account.job?.name || ''
    alertForm.handleOpenCreate(sessionJobs.some((j) => j.name === current) ? current : sessionJobs[0]?.name || '')
  }

  // The dialog creates only event alerts: saved active, no check to write.
  const handleCreate = async (body: CreateAlertBody) => {
    try {
      await createAlert(body)
    } catch (err) {
      alertForm.handleError(extractErrorMessage(err)) // the dialog stays open and says why
      return
    }
    alertForm.handleClose()
  }

  const handleSave = async (body: UpdateAlertBody) => {
    if (!editingAlert) return
    try {
      // Never sends status: activating needs a saved check.
      await updateAlert(editingAlert.id, body, { quiet: true })
    } catch (err) {
      alertForm.handleError(extractErrorMessage(err))
      return
    }
    alertForm.handleClose()
  }

  const handleChangeWithAgent = () => {
    if (!editingAlert) return
    alertForm.handleClose()
    setup.startSetup(editingAlert)
  }

  const handleSetUpCheck = (alert: AlertRule) => {
    if (setupReason || alertSetupReason(alert, viewerEmail, whoToAsk)) return
    setup.startSetup(alert)
  }

  // Confirm-gated delete: kept separate from the voiceCollection actions
  // below so the description/JSON reply can spell out the destructive
  // confirm-first flow (mirrors the confirm-first pattern used for
  // destructive/consequential server-side voice tools, e.g. delete_dashboard
  // on the Dashboards page). Resolves the alert by id from find_alert.
  const deleteAlertTool: VoiceUITool = {
    name: 'delete_alert',
    description:
      'Delete an alert, identified by the id from find_alert. This is destructive and cannot be undone, ' +
      'so it is confirm-first: call it with just the id first, read the returned confirmation message back to ' +
      'the user, and only call it again with confirmed=true once they explicitly say yes. ' +
      "Only the alert's owner or a Manager can delete it.",
    meta: { category: 'item-action', collection: 'alert', confirm: 'click' },
    parameters: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'The alert id (from find_alert).' },
        confirmed: {
          type: 'boolean',
          description: 'Set true only after the user has explicitly confirmed the deletion.',
        },
      },
      required: ['id'],
      additionalProperties: false,
    },
    execute: (args) => {
      const id = String(args.id ?? '')
      const alert = alerts.find((a) => a.id === id)
      if (!alert) {
        return JSON.stringify({ error: `no alert with id "${id}" — call find_alert first` })
      }
      const refused = colleagueAlertReason(alert, viewerEmail, isManager, whoToAsk)
      if (refused) return JSON.stringify({ error: refused })
      const name = alert.name || alert.id
      if (args.confirmed !== true) {
        return JSON.stringify({
          needs_confirmation: true,
          message: `About to delete "${name}" — say yes to confirm`,
        })
      }
      deleteAlert(alert.id)
      return JSON.stringify({ ok: true, deleted: name })
    },
  }

  // Douglas reads this back aloud, so it carries display names rather than
  // the codes in the URL (#1347 item 1).
  const names = useDisplayNames(customer)

  useVoiceRegistration({
    pageKey: `alerts:${customer}`,
    pageContext: () =>
      `Alerts list for ${names.customer}: ${mine.length} of the user's own and ${colleagues.length} of their colleagues' shown${statusFilter ? ` (filtered to status "${statusFilter}")` : ''}.`,
    uiTools: [
      ...buildVoiceTools({
        choices: [
          {
            name: 'filter_alerts_by_status',
            label: 'the alert status filter',
            options: [
              { value: '', describe: 'show all alerts, no filter' },
              'active',
              'paused',
              'archived',
            ],
            select: (v) => setStatusFilter(v as AlertRuleStatus | ''),
          },
        ],
        actions: [
          {
            name: 'create_new_alert',
            label: 'open the agent start screen with the New alert card (saved tables or a question about the job\'s data, the condition, schedule and who else to notify); nothing is saved until Send there',
            run: () => handleNewAlertClick(),
            enabled: () => !newAlertReason,
          },
          {
            name: 'watch_an_event',
            label: 'open the Watch an event form (an alert on something that happens on a job, such as new data imported; no agent)',
            run: () => handleWatchEventClick(),
          },
        ],
        collections: [
          voiceCollection<AlertRule>({
            name: 'alert',
            label: 'alert',
            list: () => alerts,
            identify: (a) => ({ id: a.id, label: a.name || a.id, keywords: a.status }),
            actions: [
              { name: 'view_alert', label: 'view', run: (a) => handleViewClick(a) },
            ],
          }),
        ],
      }),
      deleteAlertTool,
    ],
  })

  const handleViewClick = (alert: AlertRule) => {
    if (!customer) return
    router.navigate('alert-detail', {
      customer,
      alertId: alert.id,
    })
  }

  // A failed post (409 already running, 400 no script) reaches the snackbar
  // through the hook's onError, so the rejection is only swallowed here.
  const handleRunNow = (alert: AlertRule) => {
    void evaluateAlert(alert.id).catch(() => {})
  }

  const handleTogglePause = (alert: AlertRule) => {
    const nextStatus: AlertRuleStatus = alert.status === 'active' ? 'paused' : 'active'
    // An event alert has no script to need; a table or question alert resumes only with a saved check.
    if (nextStatus === 'active' && alertSourceOf(alert).kind !== 'event' && !alert.config?.check?.script_hash) return
    updateAlert(alert.id, { status: nextStatus })
  }

  const handleDeleteClick = (alert: AlertRule) => {
    setDeleteTarget(alert)
  }

  const handleConfirmDelete = () => {
    if (!deleteTarget || deleteIsLoading) return
    deleteAlert(deleteTarget.id)
    setDeleteTarget(null)
  }

  const handleCancelDelete = () => {
    if (deleteIsLoading) return
    setDeleteTarget(null)
  }

  const formatDate = (timestamp: number) => {
    if (!timestamp) return 'Never'
    return new Date(timestamp * 1000).toLocaleString()
  }

  const renderRow = (alert: AlertRuleWithLatest, showOwner: boolean) => {
    const kind = alertSourceOf(alert).kind
    const isEvent = kind === 'event'
    const hasScript = Boolean(alert.config?.check?.script_hash)
    const canResume = isEvent || hasScript
    const needsCheck = !isEvent && !hasScript
    const latest = alert.latest_evaluation
    const rowRunning = latest?.result === 'running' || evaluatingAlertId === alert.id
    // A non-Manager may look at a colleague's alert, not change it.
    const rowReason = colleagueAlertReason(alert, viewerEmail, isManager, whoToAsk)
    const checkReason = setupReason ?? alertSetupReason(alert, viewerEmail, whoToAsk)
    return (
      <TableRow key={alert.id} hover>
        <TableCell>{alert.name}</TableCell>
        {showOwner && <TableCell>{alert.user}</TableCell>}
        <TableCell>
          <Chip label={kindChipLabel(kind)} size="small" variant="outlined" />
        </TableCell>
        <TableCell>
          <Chip
            label={alert.status}
            color={STATUS_CHIP_COLOR[alert.status]}
            size="small"
          />
        </TableCell>
        <TableCell>{isEvent ? 'on event' : alert.config?.schedule ? describeSchedule(alert.config.schedule) : '—'}</TableCell>
        <TableCell>
          {latest ? (
            <Chip
              label={latest.result.replace('_', ' ')}
              color={RESULT_CHIP_COLOR[latest.result]}
              size="small"
            />
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell>{formatDate(alert.last_evaluated_at)}</TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {needsCheck && (
            <DisabledReason reason={checkReason}>
              <Button
                size="small"
                color="info"
                startIcon={<SmartToyIcon fontSize="small" />}
                onClick={() => handleSetUpCheck(alert)}
                sx={{ mr: 0.5 }}
              >
                Set up check
              </Button>
            </DisabledReason>
          )}
          {!isEvent && (
            <RowControl reason={rowReason} tooltip={rowRunning ? 'Already running' : hasScript ? 'Run the saved check now' : 'Run now needs a saved check script'}>
              <IconButton
                onClick={() => handleRunNow(alert)}
                size="small"
                color="info"
                disabled={rowRunning || !hasScript}
                aria-label={`Run ${alert.name} now`}
              >
                <RunNowIcon fontSize="small" />
              </IconButton>
            </RowControl>
          )}
          <RowControl reason={rowReason} tooltip="Edit">
            <IconButton onClick={() => alertForm.handleOpenEdit(alert)} size="small" aria-label={`Edit ${alert.name}`}>
              <EditIcon fontSize="small" />
            </IconButton>
          </RowControl>
          <Tooltip title="View">
            <IconButton onClick={() => handleViewClick(alert)} size="small" aria-label={`View ${alert.name}`}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <RowControl reason={rowReason} tooltip={alert.status === 'active' ? 'Pause' : canResume ? 'Resume' : 'Resume needs a saved check script'}>
            <IconButton
              onClick={() => handleTogglePause(alert)}
              size="small"
              color="info"
              disabled={alert.status !== 'active' && !canResume}
              aria-label={alert.status === 'active' ? `Pause ${alert.name}` : `Resume ${alert.name}`}
            >
              {alert.status === 'active' ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </RowControl>
          <RowControl reason={rowReason} tooltip="Delete">
            <IconButton onClick={() => handleDeleteClick(alert)} size="small" aria-label={`Delete ${alert.name}`}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </RowControl>
        </TableCell>
      </TableRow>
    )
  }

  const renderTable = (rows: AlertRuleWithLatest[], showOwner: boolean, emptyText: string) => {
    const columns = showOwner ? 8 : 7
    return (
      <TableContainer sx={{ backgroundColor: 'background.paper', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              {showOwner && <TableCell>Owner</TableCell>}
              <TableCell>Watches</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Last result</TableCell>
              <TableCell>Last checked</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listIsLoading ? (
              <TableRow>
                <TableCell colSpan={columns} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : listIsError ? (
              <TableRow>
                <TableCell colSpan={columns}>
                  <Alert severity="error">{listError instanceof Error ? listError.message : 'Unable to load alerts.'}</Alert>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((a) => renderRow(a, showOwner))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <PageHeader
        title="Alerts"
        subtitle={`Manage alerts for ${account.customer?.displayName || customer}`}
        actions={
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <PageAgentButton
              pageKey="alerts"
              customer={customer}
              open={helperOpen}
              onClick={() => setHelperOpen(o => !o)}
            />
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as AlertRuleStatus | '')}
                inputProps={{ 'aria-label': 'Filter by status' }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '6px' }} />
            <ActionButton icon={<EventIcon />} label="Watch an event" onClick={handleWatchEventClick} />
            <DisabledReason reason={newAlertReason}>
              <ActionButton icon={<AddIcon />} label="New alert" onClick={handleNewAlertClick} />
            </DisabledReason>
          </Box>
        }
      />

      <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: 'background.paper', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TierSection title="Mine" count={mine.length} open={mineOpen} onToggle={() => setMineOpen((o) => !o)} variant="mine">
          {renderTable(mine, false, 'No alerts yet — create one')}
        </TierSection>
        <TierSection title="My colleagues'" count={colleagues.length} open={colleaguesOpen} onToggle={() => setColleaguesOpen((o) => !o)} variant="colleagues">
          {renderTable(colleagues, true, "No colleague's alerts to show")}
        </TierSection>
      </Box>

      {alertForm.form && (
        <AlertFormDialog
          key={alertForm.formKey}
          open
          alert={editingAlert}
          jobs={sessionJobs}
          job={alertForm.job}
          onJobChange={alertForm.handleJobChange}
          eventType={alertForm.eventType}
          onEventTypeChange={alertForm.handleEventTypeChange}
          pipelines={alertForm.pipelines}
          pipelinesLoading={alertForm.pipelinesLoading}
          recipientOptions={alertForm.recipientOptions}
          recipientsLoading={alertForm.recipientsLoading}
          recipientsError={alertForm.recipientsError}
          recipientQuery={alertForm.recipientQuery}
          onRecipientQueryChange={alertForm.handleRecipientQueryChange}
          lockedReason={alertConditionLockedReason(editingAlert, viewerEmail, whoToAsk)}
          submitting={createIsLoading || updateIsLoading}
          error={alertForm.error}
          onCreate={handleCreate}
          onSave={handleSave}
          onClose={alertForm.handleClose}
          onChangeWithAgent={handleChangeWithAgent}
        />
      )}

      <DeleteConfirmWindow
        open={Boolean(deleteTarget)}
        title="Delete Alert"
        message={`Are you sure you want to delete "${deleteTarget?.name || deleteTarget?.id}"? This action cannot be undone.`}
        confirmWord="delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <PageAgentDrawer
        pageKey="alerts"
        customer={customer}
        job={account.job?.name || sessionJobs[0]?.name || ''}
        open={helperOpen}
        onClose={() => setHelperOpen(false)}
      />
    </Box>
  )
}

export default AlertsPage
